"use client";

import { List } from "lucide-react";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
} from "~/components/ui/drawer";
import { type ReactNode, useState } from "react";
import { TableOfContents } from "~/components/table-of-contents";
import { PromoContent } from "~/components/promo-content";
import { cn } from "~/lib/utils";

interface MobileTableOfContentsProps {
  /**
   * Override the drawer footer. Defaults to the therapist-matching
   * `PromoContent` upsell used by the blog. Pass `null` to render no footer
   * at all (help articles, which should not upsell therapists).
   */
  footer?: ReactNode;
  showOnDesktop?: boolean;
}

export function MobileTableOfContents({
  footer = <PromoContent />,
  showOnDesktop = false,
}: MobileTableOfContentsProps = {}) {
  const [open, setOpen] = useState(false);
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger
        aria-label="Sadržaj članka"
        className={cn(
          "bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:outline-primary fixed right-6 bottom-6 z-50 flex min-h-11 items-center gap-2 rounded-full p-3 shadow-lg transition-colors focus-visible:outline-2 focus-visible:outline-offset-4",
          showOnDesktop ? "lg:px-5" : "lg:hidden",
        )}
      >
        <List size={20} aria-hidden="true" />
        {showOnDesktop && (
          <span className="hidden text-sm font-medium lg:inline">
            Sadržaj članka
          </span>
        )}
      </DrawerTrigger>

      <DrawerContent
        className={cn(
          "flex max-h-[90vh]",
          showOnDesktop ? "mx-auto max-w-2xl" : "lg:hidden",
        )}
      >
        <DrawerHeader className="shrink-0">
          <DrawerTitle className="font-semibold">Sadržaj</DrawerTitle>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <TableOfContents onNavigate={() => setOpen(false)} />
        </div>

        {footer ? (
          <DrawerFooter className="shrink-0">{footer}</DrawerFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}
